// Armado del txt de carga masiva de la DIOT (layout 2025 del SAT, 54 campos) y su
// revision contra el "Instructivo para el armado del archivo de carga masiva" (enero 2025).
//
// Recibe el agregado por proveedor que manda el backend (dataReporte.diot) y devuelve el
// texto listo para descargar y la lista de lineas que el SAT rechazaria. No bloquea: el
// usuario decide si corrige antes de presentar. Funcion pura, sin estado ni red.

const RFC_GLOBAL = "XAXX010101000";
const RFC_EXTRANJERO = "XEXX010101000";
const RFC_VALIDO = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;

// Combinaciones de tipo de tercero y tipo de operacion que acepta el SAT.
const OPERACIONES_POR_TERCERO = {
    "04": ["02", "03", "06", "08", "85"],
    "05": ["02", "03", "07"],
    "15": ["87"],
};

const rango = (prefijo, desde, hasta) =>
    Array.from({ length: hasta - desde + 1 }, (_, i) => `${prefijo}${desde + i}`);

// Campos 8 a 53 en el orden del layout.
const CAMPOS_NUMERICOS = [
    ...rango("monto", 8, 17),
    ...rango("ivaCampo", 18, 27),
    ...rango("ivaNCampo", 28, 47),
    "ivaRetenido48",
    ...rango("monto", 49, 53),
];

// Cada valor de actos con su devolucion, su tasa y las columnas de IVA (acreditable y no
// acreditable) que el SAT compara contra el.
const CATEGORIAS = [
    { base: "monto8", devolucion: "monto9", tasa: 0.08, nombre: "región fronteriza norte", iva: ["ivaCampo18", "ivaCampo19", ...rango("ivaNCampo", 28, 31)] },
    { base: "monto10", devolucion: "monto11", tasa: 0.08, nombre: "región fronteriza sur", iva: ["ivaCampo20", "ivaCampo21", ...rango("ivaNCampo", 32, 35)] },
    { base: "monto12", devolucion: "monto13", tasa: 0.16, nombre: "16%", iva: ["ivaCampo22", "ivaCampo23", ...rango("ivaNCampo", 36, 39)] },
    { base: "monto14", devolucion: "monto15", tasa: 0.16, nombre: "importación de bienes tangibles", iva: ["ivaCampo24", "ivaCampo25", ...rango("ivaNCampo", 40, 43)] },
    { base: "monto16", devolucion: "monto17", tasa: 0.16, nombre: "importación de intangibles y servicios", iva: ["ivaCampo26", "ivaCampo27", ...rango("ivaNCampo", 44, 47)] },
];

const numeroDeCampo = (campo) => campo.match(/\d+$/)[0];

const centavos = (valor) => Math.round((Number(valor) || 0) * 100) / 100;

// Simetrico: Math.round(-2.5) daria -2.
const redondear = (valor) => Math.sign(valor) * Math.round(Math.abs(valor));

const dosDigitos = (valor) =>
    valor === undefined || valor === null || valor === "" ? "" : String(valor).padStart(2, "0");

// Un pipe o un salto de linea en un nombre romperia las columnas.
const texto = (valor, maximo) => String(valor || "").replace(/[|\r\n]+/g, " ").trim().slice(0, maximo);

const normalizarFila = (fila) => {
    const tipoTercero = dosDigitos(fila.tipoTercero);
    let tipoOperacion = dosDigitos(fila.tipoOperacion);
    let rfc = texto(fila.rfc, 13).toUpperCase();
    let idFiscal = "";
    let nombreExtranjero = "";
    let pais = "";

    if (tipoTercero === "15") {
        // El global solo admite la operacion 87 y el RFC generico.
        tipoOperacion = "87";
        rfc = RFC_GLOBAL;
    } else if (tipoTercero === "05") {
        // Para el extranjero el RFC es opcional y el generico no es una clave del SAT.
        if (rfc === RFC_EXTRANJERO) rfc = "";
        idFiscal = texto(fila.idFiscal, 40);
        nombreExtranjero = texto(fila.nombreExtranjero || fila.nombreProveedor, 300);
        pais = texto(fila.paisResidencia, 3).toUpperCase();
    }

    return {
        proveedorId: String(fila.proveedorId || ""),
        nombre: fila.nombreProveedor || "",
        tipoTercero,
        tipoOperacion,
        rfc,
        idFiscal,
        nombreExtranjero,
        pais,
        campo54: fila.campo54 || "01",
        importes: Object.fromEntries(CAMPOS_NUMERICOS.map((campo) => [campo, Number(fila[campo]) || 0])),
    };
};

// El SAT no admite dos lineas del mismo tercero y operacion. Sin identificador fiscal no se
// junta: dos proveedores sin RFC no son necesariamente el mismo.
const claveAgrupacion = (registro) => {
    if (!registro.tipoTercero) return `sin-tercero:${registro.proveedorId}`;
    const identificador = registro.rfc || (registro.idFiscal ? `${registro.idFiscal}|${registro.pais}` : "");
    if (!identificador) return `sin-identificador:${registro.proveedorId}`;
    return `${registro.tipoTercero}|${registro.tipoOperacion}|${identificador}`;
};

const agrupar = (registros) => {
    const grupos = new Map();
    for (const registro of registros) {
        const clave = claveAgrupacion(registro);
        const grupo = grupos.get(clave);
        if (!grupo) {
            grupos.set(clave, { ...registro, nombres: [registro.nombre], importes: { ...registro.importes } });
            continue;
        }
        grupo.nombres.push(registro.nombre);
        for (const campo of CAMPOS_NUMERICOS) grupo.importes[campo] += registro.importes[campo];
        if (registro.campo54 === "01") grupo.campo54 = "01";
    }
    return [...grupos.values()];
};

// Redondea a pesos. Si al redondear base e IVA por separado el IVA queda arriba de la tasa
// sobre la base (el SAT lo rechaza: "no cumple con el monto maximo"), la base se redondea
// hacia arriba; el IVA nunca se toca.
const redondearLinea = (grupo, problemas) => {
    const exactos = Object.fromEntries(CAMPOS_NUMERICOS.map((campo) => [campo, centavos(grupo.importes[campo])]));
    const valores = Object.fromEntries(CAMPOS_NUMERICOS.map((campo) => [campo, redondear(exactos[campo])]));

    for (const categoria of CATEGORIAS) {
        const iva = categoria.iva.reduce((acc, campo) => acc + valores[campo], 0);
        if (iva <= 0) continue;

        if (valores[categoria.base] <= 0) {
            problemas.push(`IVA de ${categoria.nombre} sin valor de actos (campo ${numeroDeCampo(categoria.base)} en 0)`);
            continue;
        }
        if (iva > Math.round(valores[categoria.base] * categoria.tasa)) {
            valores[categoria.base] = Math.ceil(exactos[categoria.base]);
        }
        if (iva > Math.round(valores[categoria.base] * categoria.tasa)) {
            problemas.push(
                `IVA de ${categoria.nombre} (${iva}) mayor al ${categoria.tasa * 100}% del valor de actos (${valores[categoria.base]})`
            );
        }
    }

    return valores;
};

const revisarDatosTercero = (grupo, problemas) => {
    const { tipoTercero, tipoOperacion, rfc } = grupo;

    if (!OPERACIONES_POR_TERCERO[tipoTercero]) {
        problemas.push("Sin tipo de tercero válido (¿póliza sin proveedor asignado?)");
        return;
    }
    if (!OPERACIONES_POR_TERCERO[tipoTercero].includes(tipoOperacion)) {
        problemas.push(
            `Tipo de operación ${tipoOperacion || "vacío"} no válido para el tercero ${tipoTercero} (válidos: ${OPERACIONES_POR_TERCERO[tipoTercero].join(", ")})`
        );
    }

    if (tipoTercero === "04") {
        if (!rfc) problemas.push("Falta el RFC");
        else if (rfc === RFC_GLOBAL) problemas.push("RFC genérico XAXX010101000: debe ser tipo de tercero 15 (Global)");
        else if (rfc === RFC_EXTRANJERO) problemas.push("RFC XEXX010101000: debe ser tipo de tercero 05 (Extranjero)");
        else if (!RFC_VALIDO.test(rfc)) problemas.push(`RFC con formato inválido (${rfc})`);
    }

    if (tipoTercero === "05") {
        if (rfc && !RFC_VALIDO.test(rfc)) problemas.push(`RFC con formato inválido (${rfc})`);
        if (!grupo.idFiscal) problemas.push("Falta el ID tributario del extranjero");
        if (!grupo.nombreExtranjero) problemas.push("Falta el nombre del extranjero");
        if (!grupo.pais) problemas.push("Falta el país de residencia fiscal");
        else if (grupo.pais === "ZZZ") problemas.push("País ZZZ (Otro): el SAT pide especificar el lugar y el ERP no lo captura");
    }
};

const revisarImportes = (valores, problemas) => {
    const negativos = CAMPOS_NUMERICOS.filter((campo) => valores[campo] < 0);
    if (negativos.length > 0) {
        problemas.push(`Importes negativos en los campos ${negativos.map(numeroDeCampo).join(", ")}`);
    }
    for (const categoria of CATEGORIAS) {
        if (valores[categoria.devolucion] > valores[categoria.base]) {
            problemas.push(
                `Devoluciones de ${categoria.nombre} (${valores[categoria.devolucion]}) mayores al valor de actos (${valores[categoria.base]})`
            );
        }
    }
};

const armarTxtDiot = (diot) => {
    const lineas = [];
    const avisos = [];

    for (const grupo of agrupar((diot || []).map(normalizarFila))) {
        const problemas = [];
        const valores = redondearLinea(grupo, problemas);

        // Una linea en ceros no declara nada.
        if (CAMPOS_NUMERICOS.every((campo) => valores[campo] === 0)) continue;

        revisarDatosTercero(grupo, problemas);
        revisarImportes(valores, problemas);

        lineas.push(
            [
                grupo.tipoTercero,
                grupo.tipoOperacion,
                grupo.rfc,
                grupo.idFiscal,
                grupo.nombreExtranjero,
                grupo.pais,
                "", // Especificar lugar de la jurisdiccion fiscal: solo con pais ZZZ.
                ...CAMPOS_NUMERICOS.map((campo) => valores[campo]),
                grupo.campo54,
            ].join("|")
        );

        if (problemas.length > 0) {
            avisos.push({
                linea: lineas.length,
                proveedor: [...new Set(grupo.nombres)].join(", "),
                rfc: grupo.rfc || grupo.idFiscal,
                problemas,
            });
        }
    }

    return { texto: lineas.join("\n"), avisos };
};

export default armarTxtDiot;
